import React from 'react';
import {FormProvider, useFieldArray, useForm, useFormContext} from 'react-hook-form';
import type {ProyectoAutorizacionesSolicitadasDTO, ProyectoFormDTO} from '../../../../api/Objetos';
import {autorizacionesApi} from '../../../../api/api';
import {createEmptyProyecto} from '../emptyProyecto';
import {CheckboxField, DateField, FieldGrid, NumberField, TextAreaField, TextField, ToggleField} from '../../../../ui/form/fields';
import {
    EmptyState,
    FormSection,
    ItemCard,
    buildCardTitle,
    buildSummary,
    hasPersistedId,
    useProjectReadOnly,
    BulkToggle
} from '../projectForm.helpers';
import {SectionAlerts, SectionSaveButton, useCollectionSection} from '../sectionCrudUtils';
import {useRegisterSectionSave} from '../SectionSaveCoordinator';
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';

export function AutorizacionesSection(props: { proyectoId: number; showIds?: boolean }) {
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({defaultValues: createEmptyProyecto()});
    const section = useCollectionSection({
        queryKey: ['admin', 'proyectos', 'autorizaciones'],
        proyectoId: props.proyectoId,
        getFn: autorizacionesApi.list,
        createFn: autorizacionesApi.create,
        updateFn: autorizacionesApi.update,
        deleteFn: autorizacionesApi.delete,
    });

    React.useEffect(() => {
        form.reset({
            ...createEmptyProyecto(),
            autorizacionesSolicitadas: section.data ?? [],
        });
    }, [form, section.data]);

    const handleSave = form.handleSubmit(async (value) => {
        await section.save(value.autorizacionesSolicitadas ?? []);
    });

    useRegisterSectionSave({
        id: 'autorizaciones',
        isDirty: form.formState.isDirty,
        isSaving: section.saveMutation.isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <AutorizacionesSectionFields 
                showIds={Boolean(props.showIds)}
                isLoading={section.isLoading}
                loadError={section.error}
            />
            {!readOnly && (
                <>
                    <SectionSaveButton onClick={() => void handleSave()} busy={section.saveMutation.isPending}/>
                </>
            )}
        </FormProvider>
    );
}

function AutorizacionesSectionFields(props: { showIds: boolean, isLoading: boolean, loadError: any }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const readOnly = useProjectReadOnly();
    const autorizaciones = useFieldArray<ProyectoFormDTO, 'autorizacionesSolicitadas'>({
        control: form.control,
        name: 'autorizacionesSolicitadas',
    });

    const showId = (path: string) => props.showIds && hasPersistedId(form.watch(path as never));

    const setAllPublic = (isPublic: boolean) => {
        autorizaciones.fields.forEach((field, index) => {
            form.setValue(`autorizacionesSolicitadas.${index}.publico`, isPublic, { shouldDirty: true });
        });
    };

    const currentAutorizaciones = form.watch('autorizacionesSolicitadas') || [];

    return (
        <FormSection
            id="autorizaciones"
            title={t('autorizacionesSection.title', { count: autorizaciones.fields.length })}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <BulkToggle
                        label={t('autorizacionesSection.public')}
                        fields={currentAutorizaciones}
                        onToggle={setAllPublic}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            {autorizaciones.fields.length === 0 && <EmptyState message={t('autorizacionesSection.noAuthorizations')}/>}

            {autorizaciones.fields.map((field, index) => (
                <ItemCard
                    key={field.id}
                    title={buildCardTitle(
                        t,
                        t('autorizacionesSection.authorization'),
                        index,
                        form.watch(`autorizacionesSolicitadas.${index}.asunto`),
                        form.watch(`autorizacionesSolicitadas.${index}.descripcion`),
                        form.watch(`autorizacionesSolicitadas.${index}.organismo`)
                    )}
                    summary={buildSummary(
                        t,
                        form.watch(`autorizacionesSolicitadas.${index}.organismo`),
                        form.watch(`autorizacionesSolicitadas.${index}.asunto`),
                        form.watch(`autorizacionesSolicitadas.${index}.fechaAutorizacion`)
                    )}
                    onRemove={!readOnly ? () => autorizaciones.remove(index) : undefined}
                    defaultOpen={!hasPersistedId(form.watch(`autorizacionesSolicitadas.${index}.id` as never))}
                    actions={!readOnly &&
                        <ToggleField
                            label={t('autorizacionesSection.public')}
                            name={`autorizacionesSolicitadas.${index}.publico`}
                            register={form.register}
                        />
                    }
                >
                    <FieldGrid>
                        {showId(`autorizacionesSolicitadas.${index}.id`) ? (
                            <NumberField label={t('autorizacionesSection.id')} name={`autorizacionesSolicitadas.${index}.id`}
                                         register={form.register}/>
                        ) : null}

                        <TextField label={t('autorizacionesSection.subject')} name={`autorizacionesSolicitadas.${index}.asunto`}
                                   register={form.register}/>
                        <TextField label={t('autorizacionesSection.description')} name={`autorizacionesSolicitadas.${index}.descripcion`}
                                   register={form.register}/>
                        <TextField label={t('autorizacionesSection.organization')} name={`autorizacionesSolicitadas.${index}.organismo`}
                                   register={form.register}/>
                        <DateField label={t('autorizacionesSection.authorizationDate')}
                                   name={`autorizacionesSolicitadas.${index}.fechaAutorizacion`}
                                   register={form.register}/>
                        <TextField label={t('autorizacionesSection.conditionals')} name={`autorizacionesSolicitadas.${index}.condicionales`}
                                   register={form.register}/>
                        <TextField label={t('autorizacionesSection.authorizationLink')}
                                   name={`autorizacionesSolicitadas.${index}.enlaceAutorizacion`}
                                   register={form.register}/>
                    </FieldGrid>

                    <CheckboxField label={t('autorizacionesSection.authorized')} name={`autorizacionesSolicitadas.${index}.autorizada`}
                                   register={form.register}/>
                    <TextAreaField label={t('autorizacionesSection.observations')} name={`autorizacionesSolicitadas.${index}.observaciones`}
                                   register={form.register}/>
                </ItemCard>
            ))}

            {!readOnly ? (
                <button
                    className="btn btnSecondary"
                    type="button"
                    onClick={() =>
                        autorizaciones.append({
                            asunto: '',
                            descripcion: '',
                            autorizada: false,
                            organismo: '',
                            fechaAutorizacion: '',
                            condicionales: '',
                            enlaceAutorizacion: '',
                            observaciones: '',
                            publico: false,
                        } as ProyectoAutorizacionesSolicitadasDTO)
                    }
                >
                    {t('autorizacionesSection.addAuthorization')}
                </button>
            ) : null}
        </FormSection>
    );
}
